import { useTranslation } from "@sagot/i18n";
import { Button, Select } from "@sagot/ui";
import { useAudienceLabel } from "../../deploy-knowledge/shared/use-shared-queries";
import { ACCESS_PRESETS, accessPresetOf, type AccessPreset } from "./agent-knowledge-model";

const K = "build.agentKnowledge";

interface AccessSelectProps {
  name: string;
  audiences: readonly number[];
  disabled: boolean;
  /** Id of the explanation shown when the choice is locked. */
  describedBy?: string | undefined;
  onPick: (preset: AccessPreset) => void;
}

/**
 * Who may read one source: Everyone incl. guests · Signed-in users · Staff only · Custom. The
 * choice belongs to the SOURCE, so the same value shows on every agent that uses it.
 */
export function AccessSelect({ name, audiences, disabled, describedBy, onPick }: AccessSelectProps) {
  const { t } = useTranslation();
  const labelOf = useAudienceLabel();
  const current = accessPresetOf(audiences);
  const customLabel = current === "custom" ? t(`${K}.access.customNamed`, { labels: audiences.map(labelOf).join(", ") }) : t(`${K}.access.custom`);
  return (
    <div className="flex items-center gap-2">
      <Select
        className="h-9 min-w-44"
        aria-label={t(`${K}.accessFor`, { name })}
        aria-describedby={describedBy}
        value={current}
        disabled={disabled}
        onChange={(event) => { onPick(event.target.value as AccessPreset); }}
      >
        {ACCESS_PRESETS.map((preset) => (
          <option key={preset} value={preset}>
            {preset === "custom" ? customLabel : t(`${K}.access.${preset}`)}
          </option>
        ))}
      </Select>
      {current === "custom" ? (
        <Button type="button" variant="ghost" size="sm" disabled={disabled} aria-label={t(`${K}.editCustom`, { name })} onClick={() => { onPick("custom"); }}>
          {t(`${K}.editCustomShort`)}
        </Button>
      ) : null}
    </div>
  );
}
